<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    /** Direction of the line: `horizontal` (default) fills the width · `vertical` fills the height. */
    orientation?: 'horizontal' | 'vertical'
    /** Thickness: `hairline` (default), the 1px edge · `rule`, the 2px quote bar. */
    weight?: 'hairline' | 'rule'
    /** Colour: `default`, `--color-border` · `strong`, `--color-border-strong`. */
    tone?: 'default' | 'strong'
    dashed?: boolean
    /** Hides the line from assistive technology; set false for a line that separates content. */
    decorative?: boolean
    /** Horizontal only: `true` stops the line 12px short of each edge of a panel list body, for the rule between its rows · `tile` starts it where an `AppListRow` title starts and hides it beside a hovered row. */
    inset?: boolean | 'tile'
  }>(),
  {
    orientation: 'horizontal',
    weight: 'hairline',
    tone: 'default',
    dashed: false,
    decorative: true,
    inset: false,
  },
)

const LINE_CLASSES = {
  horizontal: { hairline: 'h-0 w-full border-t', rule: 'h-0 w-full border-t-2' },
  vertical: { hairline: 'w-0 self-stretch border-l', rule: 'w-0 self-stretch border-l-2' },
} as const

const INSET_CLASSES = { hairline: 'mx-4 h-0 border-t', rule: 'mx-4 h-0 border-t-2' } as const

const TILE_INSET_CLASSES = {
  hairline: 'ms-(--list-row-title-inset) me-8 h-0 border-t',
  rule: 'ms-(--list-row-title-inset) me-8 h-0 border-t-2',
} as const

const lineClass = computed(() => {
  if (props.orientation !== 'horizontal' || props.inset === false)
    return LINE_CLASSES[props.orientation][props.weight]
  if (props.inset === 'tile')
    return [
      TILE_INSET_CLASSES[props.weight],
      'transition-opacity duration-fast ease-soft motion-reduce:transition-none beside-hovered-row:opacity-0',
    ]
  return INSET_CLASSES[props.weight]
})
</script>

<template>
  <span
    :role="decorative ? undefined : 'separator'"
    :aria-orientation="decorative ? undefined : orientation"
    :aria-hidden="decorative ? 'true' : undefined"
    :class="[
      'block shrink-0',
      lineClass,
      tone === 'strong' ? 'border-border-strong' : 'border-border',
      dashed ? 'border-dashed' : 'rounded-full',
    ]"
  />
</template>
